import HtmlToReact from 'html-to-react';
import DomPurify from 'dompurify';
import moment from 'moment';
import queryString from 'query-string';
import _ from 'lodash';

import { IsEmpty, IsArray } from './checks';
import Constants from './constants';

const htmlToReactParser = new HtmlToReact.Parser();

export const convertDataURLtoBlob = (dataurl) => {
  const arr = dataurl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  // eslint-disable-next-line no-plusplus
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
};

export const ConvertDateToSGTime = (date) => {
  if (!(date instanceof Date)) {
    throw new Error('ConvertDateToSGTime(): input date is not a Date type');
  }
  return new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Singapore' }));
};

export const ConvertStartDate = (date) => {
  if (IsEmpty(date)) {
    throw new Error('ConvertStartDate(): input date is required');
  }

  return (new Date(date)).toISOString().replace('T', ' ').replace('Z', '+08');
};

export const ConvertEndDate = (date) => {
  if (IsEmpty(date)) {
    throw new Error('ConvertEndDate(): input date is required');
  }

  return (new Date(date)).toISOString().replace('T', ' ').replace('00:00:00', '23:59:59')
    .replace('Z', '+08');
};

export const ConvertToPascalCase = (string) => {
  string = Trim(string, false, true);
  if (!IsEmpty(string)) {
    return string
      .split(' ')
      .map((word) => word[0]
        .toUpperCase()
        .concat(word.slice(1).toLowerCase()))
      .join(' ');
  } else {
    return string;
  }
};

export const environment = () => {
  let stage = '';
  const envURL = new URL(process.env.REACT_APP_API_URL);
  if (envURL.hostname === 'localhost' || envURL.hostname.includes('192.168.')) {
    stage = 'LOCAL';
  } else if (envURL.hostname === 'api.monstyr.com') {
    stage = 'PROD';
  } else {
    stage = 'UAT';
  }

  return stage;
};

export const EscapeSingleQuote = (str = '') => {
  if (!IsEmpty(str)) {
    return str.replace(/\'/g, "''");
  } else { return str; }
};

// E.g. urlQueryStr = '?upgradePlan=true'
export const ExtractParamsFromUrlQueryStr = (urlQueryStr = '') => {
  if (IsEmpty(urlQueryStr)) {
    return null;
  }
  return queryString.parse(urlQueryStr);
};

export const formatDateToDayMonthNameYear = (date, hasTime = false) => {
  let dateFormat = 'D MMM YYYY';
  const timeFormat = 'HH:mm';

  if (hasTime) {
    dateFormat = `${dateFormat} ${timeFormat}`;
  }

  return (date ? moment(date).format(dateFormat) : null);
};

export const getThumbnailFromVideoURL = (videoUrl) => videoUrl.replace(`${(videoUrl.split('/')[videoUrl.split('/').length - 1]).split('.')[0]}.mp4`, `thumbnails/${(videoUrl.split('/')[videoUrl.split('/').length - 1]).split('.')[0]}.jpg`);

export const StringToInteger = (string) => {
  if (/^[-+]?(\d+)$/.test(string)) {
    return Number(string);
  } else {
    return NaN;
  }
};

export const showDateInSGTime = (date) => (date ? moment(date).utcOffset(+8, false).format('YYYY-MM-DD HH:mm') : null);

export const Sleep = (ms = 100) => new Promise((resolve) => setTimeout(resolve, ms));

export const Trim = (str = '', trimMiddle = false, trimMiddleToSingleSpace = false) => {
  if (!IsEmpty(str)) {
    str = str.trim();
    if (trimMiddleToSingleSpace) {
      str = str.replace(/\s\s+/g, ' ');
    }
    if (trimMiddle) {
      str = str.replace(/\s+/g, '');
    }
    return str;
  } else { return str; }
};

export const StringToLowercase = (string) => string.toLowerCase();

export function convertDegreesToRadians(degrees) {
  return degrees * (Math.PI / 180);
}

export function convertRadiansToDegrees(radians) {
  return radians * (180 / Math.PI);
}

export function computeOffset(location, meter, degree = 0) {
  const EARTH_RADIUS = 6371009;

  const distance = meter / EARTH_RADIUS;
  const heading = convertDegreesToRadians(90 - degree);
  const fromLat = convertDegreesToRadians(location.lat);
  const fromLng = convertDegreesToRadians(location.lng);

  const cosDistance = Math.cos(distance);
  const sinDistance = Math.sin(distance);
  const sinFromLat = Math.sin(fromLat);
  const cosFromLat = Math.cos(fromLat);
  const sinLat = cosDistance * sinFromLat + sinDistance * cosFromLat * Math.cos(heading);

  const dLng = Math.atan2(sinDistance * cosFromLat * Math.sin(heading), cosDistance - sinFromLat * sinLat);
  return { lat: convertRadiansToDegrees(Math.asin(sinLat)), lng: convertRadiansToDegrees(fromLng + dLng) };
}

export const calculateEdgeInitialCost = (waypointAType, waypointBType) => {
  const types = [waypointAType, waypointBType];
  let cost = 1;

  if (types.includes(Constants.WaypointType.Lift)) {
    cost = 1;
  } else if (types.includes(Constants.WaypointType.Escalator)) {
    cost = 2;
  } else if (types.includes(Constants.WaypointType.Staircase)) {
    cost = 3;
  }

  return cost;
};

export const swapArray = (arr, index1, index2) => {
  [arr[index1], arr[index2]] = [arr[index2], arr[index1]];
};

export const formatAddresses = (addresses) => {
  const formattedAddresses = [];

  addresses.forEach((address) => {
    const indexOfAddress = formattedAddresses.findIndex((e) => e.name === address.name);
    const hasPostalCode = !IsEmpty(address.postalCode);

    if (indexOfAddress === -1) {
      formattedAddresses.push({
        name:        address.name,
        postalCodes: [address.postalCode],
        hasPostalCode,
      });
    } else {
      // check postalCode
      if (!formattedAddresses[indexOfAddress].hasPostalCode) {
        formattedAddresses[indexOfAddress].hasPostalCode = hasPostalCode;
      }

      formattedAddresses[indexOfAddress].postalCodes.push(address.postalCode);
    }
  });

  return formattedAddresses;
};

export const convertTime12to24 = (time, isFrom2DigitHoursAndMins = false) => {
  if (isFrom2DigitHoursAndMins) {
    return moment(time, 'hh:mm A').format('HH:mm');
  } else {
    return moment(time, 'h:m A').format('HH:mm');
  }
};

export const convertTime24to12 = (time, isReturn2DigitHoursAndMins = false) => {
  if (isReturn2DigitHoursAndMins) {
    return moment(time, 'HH:mm').format('hh:mm A');
  } else {
    return moment(time, 'HH:mm').format('h:m A');
  }
};

export const ElipsisString = (str, lengthToStartElipsis) => {
  if (IsEmpty(str) || String(str).length < lengthToStartElipsis) {
    return str;
  }

  return `${String(str).substring(0, lengthToStartElipsis - 1)}...`;
};

export function isHexColour(str) {
  if (IsEmpty(str)) {
    return false;
  }

  return /^#(([0-9A-Fa-f]{2}){3,4}|[0-9A-Fa-f]{3})$/.test(str);
}

export const formatTag = (obj) => String(obj).replace(/(\n)/gm, '').trim().toLowerCase();

/**
 * @param {*} arr array of strings to sort
 * @returns `arr` sorted by alphanumeric characters and symbols
 */
export const SortByAlphaNumericAndSymbols = (arr) => _.sortBy(arr, (str) => {
  const firstChar = str.trim()[0];
  if (/[a-zA-Z]/.test(firstChar)) {
    return `1-${str.toLowerCase()}`;
  }
  if (/[0-9]/.test(firstChar)) {
    return `2-${str}`;
  }
  return `3-${str}`;
});

export const ParseHtmlIntoReactCom = (htmlStr = '') => {
  const safeHtml = DomPurify.sanitize(htmlStr);
  return htmlToReactParser.parse(safeHtml);
};

/**
 * This function is used to stringify the query params for the GET request url.
 * If u need to use it, ensure it works for your case as it doesn't stringify some objects (e.g array).
 * @param {*} obj
 * @returns
 */
export const stringifyObjectValuesForUrl = (obj) => (IsEmpty(obj) ? obj : Object.entries(obj).reduce((acc, [key, value]) => {
  // Note: we don't want to stringify the array else server doesn't receive
  acc[key] = ((typeof value === 'object' || typeof value === 'number') && !IsArray(value) && value !== null)
    ? JSON.stringify(value)
    : value;
  return acc;
}, {}));

export const getNextAlignedTime = (intervalMinutes = 30, fromTime = moment()) => {
  const m = moment(fromTime);
  const minutes = m.minutes();
  const next = Math.ceil(minutes / intervalMinutes) * intervalMinutes;

  if (next === minutes && m.seconds() === 0) {
    m.add(intervalMinutes, 'minutes');
  } else {
    m.minutes(next % 60).seconds(0).milliseconds(0);
    if (next >= 60) m.add(1, 'hour');
  }

  return m;
};

export const getReceiptAutoprocessScheduledAt = (updatedAt) => {
  try {
    // only records that were updated more than 30 minutes ago will be processed at the next 30 minute mark
    const period = 30; // minutes
    const now = moment();
    const thirtyMinutesLater = moment(updatedAt).add(period, 'minutes');
    const lastAutoProcessOtd = moment().utcOffset('+08').startOf('day').add(18.5, 'hours'); // last run of the day

    const getNextScheduledExecutionFrom = (date) => {
      let nextAlignedTime = getNextAlignedTime(period, date);

      // if past last run of the day (after 6:30 PM), schedule next day at 10:00 AM
      if (nextAlignedTime > lastAutoProcessOtd) {
        nextAlignedTime = moment().utcOffset('+08')
          .add(1, 'days').startOf('day')
          .add(10, 'hours');
      }

      // if the next run is on a weekend, jump to next Monday 10:00 AM
      const day = nextAlignedTime.isoWeekday(); // 1 = Mon, 7 = Sun
      if (day >= 6) { // Saturday (6) or Sunday (7)
        const daysToAdd = 8 - day; // +2 if Sat, +1 if Sun
        nextAlignedTime = nextAlignedTime
          .utcOffset('+08')
          .add(daysToAdd, 'days')
          .startOf('day')
          .add(10, 'hours');
      }

      return nextAlignedTime;
    };

    const scheduledNext = getNextScheduledExecutionFrom(thirtyMinutesLater);
    const processedAt = now > scheduledNext
      ? getNextScheduledExecutionFrom(now)
      : scheduledNext;

    return processedAt;
  } catch {
    return null;
  }
};

/**
 * Returns null if user didn't give permission to access location
 * Else, returns {lat, lng}
 */
const getCurrentLocation = async (timeout = 3000) => {
  try {
    if (!navigator.geolocation) {
      throw new Error('Geolocation is not supported by your browser');
    } else {
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({ lat: position.coords.latitude, lng: position.coords.longitude });
          }, (error) => {
            console.log('Util.js -> getCurrentLocation() - Error getting current location: ', error);
            reject(error);
          }, {
            maximumAge: 12000,
            timeout,
          },
        );
      });
    }
  } catch (error) {
    console.log('Util.js -> getCurrentLocation(): ', error);
    throw new Error(Constants.Error.UnexpectedError);
  }
};

/**
 * Returns null if user didn't give permission to access location
 * Else, returns {lat, lng}
 */
export const GetUserCurrentLocation = async (timeout = 3000) => {
  try {
    // request and get current location
    return getCurrentLocation(timeout);
  } catch (err) {
    console.log('Error GetUserCurrentLocation(): ', err);
    throw new Error(err);
  }
};

export const isCoordOutOfSG = (coord) => {
  if (IsEmpty(coord) || IsEmpty(coord.lat) || IsEmpty(coord.lng)) {
    return true;
  }
  return (coord.lat < 1.220782816495816 || coord.lat > 1.4679429323707653
    || coord.lng < 103.56107025042246 || coord.lng > 104.08852084727206
  );
};

export const formatDateToMonthDayYearHourMinute = (timestamp) => {
  if (IsEmpty(timestamp)) {
    return '-';
  }

  return `${new Date(ConvertDateToSGTime(new Date(timestamp))).toLocaleDateString()} ${new Date(ConvertDateToSGTime(new Date(timestamp))).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const Util = {
  convertDataURLtoBlob,
  ConvertDateToSGTime,
  ConvertStartDate,
  ConvertEndDate,
  ConvertToPascalCase,
  environment,
  EscapeSingleQuote,
  ExtractParamsFromUrlQueryStr,
  formatDateToDayMonthNameYear,
  formatAddresses,
  getNextAlignedTime,
  getThumbnailFromVideoURL,
  ParseHtmlIntoReactCom,
  showDateInSGTime,
  Sleep,
  StringToInteger,
  Trim,
  StringToLowercase,
  SortByAlphaNumericAndSymbols,
  convertDegreesToRadians,
  convertRadiansToDegrees,
  computeOffset,
  calculateEdgeInitialCost,
  swapArray,
  convertTime12to24,
  convertTime24to12,
  ElipsisString,
  isHexColour,
  formatTag,
  stringifyObjectValuesForUrl,
  GetUserCurrentLocation,
  isCoordOutOfSG,
  formatDateToMonthDayYearHourMinute,
};

export default Util;
